import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { Link } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Lightbulb,
  Pause,
  Play,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import SectionBadge from "@/components/SectionBadge";
import { trpc } from "@/lib/trpc";
import ResponsiveImage from "@/components/ResponsiveImage";

const COMMUNITY_STORIES = [
  {
    id: "happy-hour",
    text: "Happy hour conversations turn first introductions into relationships that keep moving.",
    image: "/media/events/membership-happy-hour.webp",
    position: "center 34%",
  },
  {
    id: "micro-learning",
    text: "Micro-learning on AI gives members a practical idea they can use before the week is over.",
    image: "/media/events/featured-session-stage.webp",
    position: "center center",
  },
  {
    id: "salons",
    text: "Small-room salons create the kind of candid exchange that changes how you see a challenge.",
    image: "/media/events/salon-networking.webp",
    position: "34% center",
  },
  {
    id: "expertise",
    text: "Share expertise, practice the idea out loud, and leave with momentum.",
    image: "/media/events/share-your-expertise.webp",
    position: "68% center",
  },
  {
    id: "executive-dinner",
    text: "Curated dinners make space for big questions, thoughtful peers, and useful perspective.",
    image: "/media/events/executive-dinner.webp",
    position: "center center",
  },
] as const;

const ANNOUNCEMENT_FALLBACK_IMAGES = [
  "/media/events/featured-session-stage.webp",
  "/media/events/salon-networking.webp",
  "/media/events/share-your-expertise.webp",
] as const;

const MEMBERSHIP_VALUES: Array<{
  label: string;
  detail: string;
  accent: string;
  Icon: LucideIcon;
}> = [
  {
    label: "People who move work forward",
    detail: "Meet the right room.",
    accent: "#F472B6",
    Icon: Users,
  },
  {
    label: "Ideas you can use",
    detail: "Turn insight into momentum.",
    accent: "#86EFAC",
    Icon: Lightbulb,
  },
  {
    label: "A calendar worth joining",
    detail: "Stay close to the next conversation.",
    accent: "#FCD34D",
    Icon: CalendarDays,
  },
];

type HeroSlide =
  | { kind: "membership"; id: "membership" }
  | {
      kind: "community";
      id: string;
      statement: string;
      image: string;
      position: string;
    }
  | {
      kind: "announcement";
      id: string;
      category: string;
      title: string;
      body: string;
      href?: string;
      image: string;
    };

function isSafeInternalPath(value: string | null | undefined): value is string {
  const path = value?.trim();
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.includes("\\") || /[\u0000-\u001f]/.test(path)) {
    return false;
  }
  try {
    return new URL(path, "https://chi-alliance.invalid").origin === "https://chi-alliance.invalid";
  } catch {
    return false;
  }
}

function loopIndex(index: number, length: number): number {
  if (length < 1) return 0;
  return ((index % length) + length) % length;
}

export default function HomeMembershipCommunityHero() {
  const announcements = trpc.notifications.announcements.useQuery(
    { limit: 6 },
    { staleTime: 60_000, retry: false },
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [touchPaused, setTouchPaused] = useState(false);
  const resumeTimerRef = useRef<number | null>(null);
  const id = useId().replace(/:/g, "");
  const isPaused = isHovering || hasFocus || userPaused || touchPaused;

  const slides = useMemo<HeroSlide[]>(() => {
    const recentAnnouncements: HeroSlide[] = (announcements.data ?? []).map((item, index) => ({
      kind: "announcement",
      id: `announcement-${item.id}`,
      category: "Community announcement",
      title: item.title,
      body: item.body,
      href: isSafeInternalPath(item.href) ? item.href : undefined,
      image: ANNOUNCEMENT_FALLBACK_IMAGES[index % ANNOUNCEMENT_FALLBACK_IMAGES.length],
    }));

    return [
      { kind: "membership", id: "membership" },
      ...COMMUNITY_STORIES.map(story => ({
        kind: "community" as const,
        id: story.id,
        statement: story.text,
        image: story.image,
        position: story.position,
      })),
      ...recentAnnouncements,
    ];
  }, [announcements.data]);

  const activeSlide = slides[activeIndex] ?? slides[0];
  const announcementCount = announcements.data?.length ?? 0;
  const announcementStatus = announcements.isLoading
    ? "Loading the latest community updates."
    : announcements.error
      ? "Latest community updates are temporarily unavailable. The member and community stories remain available."
      : announcementCount === 0
        ? "New community updates will appear here as they are published."
        : `${announcementCount} recent community ${announcementCount === 1 ? "update" : "updates"} added.`;

  useEffect(() => {
    setActiveIndex(current => loopIndex(current, slides.length));
  }, [slides.length]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setReducedMotion(media.matches);
    updateMotionPreference();
    media.addEventListener("change", updateMotionPreference);
    return () => media.removeEventListener("change", updateMotionPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion || isPaused || slides.length < 2) return;
    const timer = window.setInterval(() => {
      setActiveIndex(current => loopIndex(current + 1, slides.length));
    }, 8_500);
    return () => window.clearInterval(timer);
  }, [isPaused, reducedMotion, slides.length]);

  useEffect(() => () => {
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
  }, []);

  const pauseForTouch = () => {
    setTouchPaused(true);
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = window.setTimeout(() => setTouchPaused(false), 5_000);
  };

  const goToSlide = (index: number) => {
    setActiveIndex(loopIndex(index, slides.length));
    pauseForTouch();
  };

  const focusTab = (index: number) => {
    window.requestAnimationFrame(() => document.getElementById(`${id}-tab-${index}`)?.focus());
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    let nextIndex: number;
    if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = slides.length - 1;
    } else {
      nextIndex = (index + (event.key === "ArrowRight" ? 1 : -1) + slides.length) % slides.length;
    }
    goToSlide(nextIndex);
    focusTab(nextIndex);
  };

  const linkTabIndex = (isActive: boolean) => (isActive ? 0 : -1);

  return (
    <section
      id="community-story"
      className="home-membership-community-hero"
      aria-label="Membership and community highlights"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onFocusCapture={() => setHasFocus(true)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHasFocus(false);
      }}
    >
      <div className="home-membership-community-hero__slides">
        {slides.map((slide, index) => {
          const isActive = activeIndex === index;

          return (
            <article
              id={`${id}-slide-${slide.id}`}
              className={`home-membership-community-hero__slide home-membership-community-hero__slide--${slide.kind}${isActive ? " is-active" : ""}`}
              key={slide.id}
              role="tabpanel"
              aria-hidden={!isActive}
              aria-labelledby={`${id}-tab-${index}`}
            >
              {slide.kind !== "membership" && (
                <>
                  <ResponsiveImage
                    className="home-membership-community-hero__photo-fill"
                    src={slide.image}
                    alt=""
                    aria-hidden="true"
                    maxWidth={1600}
                    sizes="100vw"
                  />
                  <div className="home-membership-community-hero__photo-safe-stage">
                    <ResponsiveImage
                      className="home-membership-community-hero__photo-safe"
                      src={slide.image}
                      alt=""
                      aria-hidden="true"
                      loading={isActive ? "eager" : "lazy"}
                      decoding="async"
                      maxWidth={1600}
                      sizes="100vw"
                      style={{ objectPosition: slide.kind === "community" ? slide.position : "center center" }}
                    />
                  </div>
                </>
              )}
              <div className="home-membership-community-hero__image-wash" aria-hidden="true" />
              <div className="home-membership-community-hero__content container">
                {slide.kind === "membership" ? (
                  <>
                    <div className="home-membership-community-hero__copy">
                      <SectionBadge>Membership</SectionBadge>
                      <p className="home-membership-community-hero__kicker">A profile opens the door to more.</p>
                      <h2>Find your next <span>connection.</span></h2>
                      <p className="home-membership-community-hero__body">
                        A CHI Alliance membership brings the right people, practical ideas, and a calendar worth joining into one place.
                      </p>
                      <div className="chi-cta-pair home-membership-community-hero__actions">
                        <Link
                          href="/create-account?source=home-membership"
                          className="chi-btn-primary"
                          tabIndex={linkTabIndex(isActive)}
                        >
                          Join free <Users size={16} aria-hidden="true" />
                        </Link>
                        <Link
                          href="/membership"
                          className="chi-btn-outline"
                          tabIndex={linkTabIndex(isActive)}
                        >
                          Explore benefits <ArrowRight size={15} aria-hidden="true" />
                        </Link>
                      </div>
                      <p className="home-membership-community-hero__announcement-status" role="status">
                        {announcementStatus}
                      </p>
                    </div>
                    <div className="home-membership-community-hero__values" aria-label="Membership value points">
                      <p className="home-membership-community-hero__values-heading">Membership is a way in.</p>
                      {MEMBERSHIP_VALUES.map(({ label, detail, accent, Icon }) => (
                        <div className="home-membership-community-hero__value" key={label}>
                          <span style={{ "--value-accent": accent } as CSSProperties}><Icon size={20} aria-hidden="true" /></span>
                          <div><strong>{label}</strong><small>{detail}</small></div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : slide.kind === "community" ? (
                  <div className="home-membership-community-hero__copy home-membership-community-hero__copy--story">
                    <p className="home-membership-community-hero__eyebrow">The Community</p>
                    <h2>Where bold conversations <span>become opportunities.</span></h2>
                    <p className="home-membership-community-hero__story">{slide.statement}</p>
                    <Link
                      href="/community"
                      className="home-membership-community-hero__text-link"
                      tabIndex={linkTabIndex(isActive)}
                    >
                      Explore the community <ArrowRight size={15} aria-hidden="true" />
                    </Link>
                  </div>
                ) : (
                  <div className="home-membership-community-hero__copy home-membership-community-hero__copy--announcement">
                    <p className="home-membership-community-hero__eyebrow">{slide.category}</p>
                    <h2>{slide.title}</h2>
                    <p className="home-membership-community-hero__story">{slide.body}</p>
                    {slide.href ? (
                      <Link
                        href={slide.href}
                        className="home-membership-community-hero__text-link"
                        tabIndex={linkTabIndex(isActive)}
                      >
                        Read the update <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                    ) : (
                      <Link
                        href="/community"
                        className="home-membership-community-hero__text-link"
                        tabIndex={linkTabIndex(isActive)}
                      >
                        Explore the community <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="home-membership-community-hero__sr-status" aria-live="polite">
        {activeSlide && `Showing ${activeSlide.kind === "membership" ? "membership" : activeSlide.kind === "community" ? "a community story" : `community update: ${activeSlide.title}`}, slide ${activeIndex + 1} of ${slides.length}.`}
      </div>

      <div className="home-membership-community-hero__controls">
        <button
          type="button"
          className="home-membership-community-hero__arrow"
          onClick={() => goToSlide(activeIndex - 1)}
          aria-label="Previous slide"
        >
          <ArrowLeft size={17} aria-hidden="true" />
        </button>
        <div className="home-membership-community-hero__dots" role="tablist" aria-label="Membership and community slides">
          {slides.map((slide, index) => (
            <button
              id={`${id}-tab-${index}`}
              type="button"
              role="tab"
              key={slide.id}
              aria-selected={activeIndex === index}
              aria-label={`Show slide ${index + 1}${slide.kind === "membership" ? ", membership" : slide.kind === "community" ? ", community story" : `, ${slide.title}`}`}
              aria-controls={`${id}-slide-${slide.id}`}
              className={activeIndex === index ? "is-active" : ""}
              tabIndex={activeIndex === index ? 0 : -1}
              onClick={() => goToSlide(index)}
              onKeyDown={event => handleTabKeyDown(event, index)}
            />
          ))}
        </div>
        <button
          type="button"
          className="home-membership-community-hero__arrow"
          onClick={() => goToSlide(activeIndex + 1)}
          aria-label="Next slide"
        >
          <ArrowRight size={17} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="home-membership-community-hero__motion-state"
          disabled={reducedMotion}
          aria-label={reducedMotion ? "Autoplay disabled by reduced motion preference" : userPaused ? "Resume automatic slide rotation" : "Pause automatic slide rotation"}
          aria-pressed={userPaused}
          onClick={() => setUserPaused(current => !current)}
        >
          {reducedMotion ? <><Pause size={12} aria-hidden="true" /> Motion off</> : userPaused ? <><Play size={12} aria-hidden="true" /> Play</> : <><Pause size={12} aria-hidden="true" /> Pause</>}
        </button>
      </div>

      <style>{`
        .home-membership-community-hero{position:relative;min-height:clamp(35rem,50vw,43rem);overflow:hidden;background:#080C18;border-top:1px solid rgba(255,255,255,.08);border-bottom:1px solid rgba(255,255,255,.08);isolation:isolate}
        .home-membership-community-hero__slides,.home-membership-community-hero__slide{position:absolute;inset:0}
        .home-membership-community-hero__slide{display:flex;opacity:0;pointer-events:none;background-color:#0B1020;transition:opacity .8s cubic-bezier(.23,1,.32,1)}
        .home-membership-community-hero__slide.is-active{opacity:1;pointer-events:auto;z-index:1}
        .home-membership-community-hero__slide--membership{background:radial-gradient(circle at 76% 20%,rgba(233,30,140,.32),transparent 23%),radial-gradient(circle at 24% 80%,rgba(0,212,255,.16),transparent 28%),linear-gradient(120deg,#0D1220 8%,#1B1330 54%,#081B2D 100%)}
        .home-membership-community-hero__photo-fill,.home-membership-community-hero__photo-safe-stage{position:absolute;inset:0;width:100%;height:100%;pointer-events:none}
        .home-membership-community-hero__photo-fill{z-index:0;display:block;object-fit:cover;object-position:center;filter:blur(18px);opacity:.72;transform:scale(1.08)}
        .home-membership-community-hero__photo-safe-stage{z-index:1;display:flex;align-items:center;justify-content:center}
        .home-membership-community-hero__photo-safe{display:block;width:auto;height:auto;max-width:100%;max-height:100%;object-fit:contain;object-position:center;opacity:.9;filter:saturate(.92) contrast(.94);mask-image:linear-gradient(to right,transparent 0%,#000 10%,#000 90%,transparent 100%),linear-gradient(to bottom,transparent 0%,#000 10%,#000 90%,transparent 100%);mask-composite:intersect;-webkit-mask-image:linear-gradient(to right,transparent 0%,#000 10%,#000 90%,transparent 100%),linear-gradient(to bottom,transparent 0%,#000 10%,#000 90%,transparent 100%);-webkit-mask-composite:source-in}
        .home-membership-community-hero__image-wash{position:absolute;z-index:2;inset:0;background:linear-gradient(90deg,rgba(5,9,20,.98) 0%,rgba(5,9,20,.9) 36%,rgba(5,9,20,.52) 66%,rgba(5,9,20,.72) 100%),linear-gradient(0deg,rgba(5,9,20,.9),transparent 45%,rgba(5,9,20,.35));pointer-events:none}
        .home-membership-community-hero__slide--membership .home-membership-community-hero__image-wash{background:radial-gradient(ellipse at 72% 38%,rgba(233,30,140,.11),transparent 32%),linear-gradient(90deg,rgba(5,9,20,.58),rgba(5,9,20,.06) 68%,rgba(5,9,20,.48))}
        .home-membership-community-hero__content{position:relative;z-index:3;display:grid;grid-template-columns:minmax(0,1.1fr) minmax(20rem,.9fr);align-items:center;gap:clamp(2rem,7vw,7rem);width:100%;min-height:100%;box-sizing:border-box;padding-block:clamp(6.5rem,10vw,8.5rem) clamp(5rem,7vw,6rem)}
        .home-membership-community-hero__copy{max-width:38rem}
        .home-membership-community-hero__copy .section-badge{margin-bottom:1.15rem}
        .home-membership-community-hero__kicker,.home-membership-community-hero__eyebrow{margin:0 0 .7rem;color:#F9A8D4;font-family:'Space Grotesk',sans-serif;font-size:.72rem;font-weight:800;letter-spacing:.13em;text-transform:uppercase}
        .home-membership-community-hero__copy h2{margin:0;color:#fff;font-family:'DM Serif Display',serif;font-size:clamp(2.7rem,5.3vw,5.25rem);font-weight:400;letter-spacing:-.045em;line-height:.98;text-shadow:0 8px 30px rgba(0,0,0,.42)}
        .home-membership-community-hero__copy h2 span{background:linear-gradient(100deg,#F5A623,#FB7185 48%,#E91E8C);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
        .home-membership-community-hero__body{max-width:31rem;margin:1.25rem 0 1.75rem;color:#E2E8F0;font-family:'Inter',sans-serif;font-size:var(--chi-body);line-height:1.65}
        .home-membership-community-hero__actions{align-items:center}
        .home-membership-community-hero__actions a{display:inline-flex;align-items:center;justify-content:center;gap:.45rem;text-decoration:none}
        .home-membership-community-hero__announcement-status{margin:1.2rem 0 0;color:#94A3B8;font-family:'Inter',sans-serif;font-size:.75rem;line-height:1.5}
        .home-membership-community-hero__values{display:grid;gap:.9rem;padding:clamp(1.2rem,2.5vw,2rem);border:1px solid rgba(255,255,255,.14);border-radius:1.35rem;background:linear-gradient(145deg,rgba(30,35,56,.72),rgba(8,12,24,.78));box-shadow:inset 0 1px 0 rgba(255,255,255,.07),0 28px 65px rgba(0,0,0,.3);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px)}
        .home-membership-community-hero__values-heading{margin:0 0 .35rem;color:#F5A623;font-family:'Space Grotesk',sans-serif;font-size:.68rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
        .home-membership-community-hero__value{display:flex;align-items:center;gap:.8rem;padding:.65rem 0;color:#fff}
        .home-membership-community-hero__value+ .home-membership-community-hero__value{border-top:1px solid rgba(255,255,255,.08)}
        .home-membership-community-hero__value>span{display:grid;width:2.8rem;height:2.8rem;flex:0 0 auto;place-items:center;border:1px solid color-mix(in srgb,var(--value-accent) 60%,transparent);border-radius:.85rem;color:var(--value-accent);background:color-mix(in srgb,var(--value-accent) 13%,transparent)}
        .home-membership-community-hero__value strong,.home-membership-community-hero__value small{display:block}
        .home-membership-community-hero__value strong{font-family:'Space Grotesk',sans-serif;font-size:.93rem;font-weight:700;line-height:1.25}
        .home-membership-community-hero__value small{margin-top:.18rem;color:#94A3B8;font-family:'Inter',sans-serif;font-size:.75rem}
        .home-membership-community-hero__copy--story{align-self:end;padding-bottom:1rem}
        .home-membership-community-hero__copy--story h2{max-width:35rem;font-size:clamp(2.5rem,5vw,5rem)}
        .home-membership-community-hero__story{max-width:34rem;margin:1.4rem 0 1.45rem;color:#F1F5F9;font-family:'Inter',sans-serif;font-size:clamp(1rem,1.6vw,1.25rem);line-height:1.6;text-shadow:0 3px 14px rgba(0,0,0,.82)}
        .home-membership-community-hero__copy--announcement{max-width:39rem;align-self:center}
        .home-membership-community-hero__copy--announcement h2{font-size:clamp(2.3rem,4.7vw,4.6rem)}
        .home-membership-community-hero__copy--announcement h2,.home-membership-community-hero__copy--announcement .home-membership-community-hero__story{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical}
        .home-membership-community-hero__copy--announcement h2{-webkit-line-clamp:3}
        .home-membership-community-hero__copy--announcement .home-membership-community-hero__story{-webkit-line-clamp:5}
        .home-membership-community-hero__text-link{display:inline-flex;align-items:center;gap:.4rem;color:#FBCFE8;font-family:'Inter',sans-serif;font-size:.83rem;font-weight:750;text-decoration:none;text-shadow:0 2px 10px rgba(0,0,0,.9)}
        .home-membership-community-hero__text-link:hover,.home-membership-community-hero__text-link:focus-visible{color:#fff}
        .home-membership-community-hero__controls{position:absolute;z-index:4;right:clamp(1rem,4vw,3.5rem);bottom:clamp(1rem,3vw,2.2rem);left:clamp(1rem,4vw,3.5rem);display:flex;align-items:center;justify-content:center;gap:.7rem}
        .home-membership-community-hero__arrow{display:grid;width:2.75rem;height:2.75rem;flex:0 0 auto;place-items:center;border:1px solid rgba(255,255,255,.2);border-radius:50%;color:#fff;background:rgba(8,12,24,.64);cursor:pointer;transition:background .2s,border-color .2s,transform .2s}
        .home-membership-community-hero__arrow:hover,.home-membership-community-hero__arrow:focus-visible{border-color:rgba(249,168,212,.75);background:rgba(233,30,140,.32);outline:0;transform:translateY(-2px)}
        .home-membership-community-hero__dots{display:flex;align-items:center;justify-content:center;gap:.42rem;max-width:min(60vw,38rem);overflow:auto;padding:.15rem .2rem}
        .home-membership-community-hero__dots button{position:relative;width:1.45rem;height:2.75rem;flex:0 0 auto;padding:0;border:0;border-radius:999px;background:transparent;cursor:pointer;transition:width .2s}
        .home-membership-community-hero__dots button::after{position:absolute;top:50%;left:50%;width:.55rem;height:.55rem;content:"";border-radius:50%;background:rgba(255,255,255,.34);transform:translate(-50%,-50%);transition:width .2s,background .2s}
        .home-membership-community-hero__dots button:hover,.home-membership-community-hero__dots button:focus-visible{outline:0}
        .home-membership-community-hero__dots button:hover::after,.home-membership-community-hero__dots button:focus-visible::after{background:#fff;box-shadow:0 0 0 4px rgba(255,255,255,.12)}
        .home-membership-community-hero__dots button.is-active{width:2.8rem}
        .home-membership-community-hero__dots button.is-active::after{width:2.1rem;border-radius:999px;background:linear-gradient(90deg,#F5A623,#E91E8C)}
        .home-membership-community-hero__motion-state{display:inline-flex;min-height:2.75rem;align-items:center;justify-content:center;gap:.28rem;padding:0 .65rem;border:1px solid rgba(255,255,255,.13);border-radius:999px;color:#CBD5E1;background:rgba(8,12,24,.64);font-family:'Space Grotesk',sans-serif;font-size:.6rem;font-weight:700;letter-spacing:.07em;text-transform:uppercase;cursor:pointer}
        .home-membership-community-hero__motion-state:hover,.home-membership-community-hero__motion-state:focus-visible{border-color:rgba(249,168,212,.65);color:#fff;outline:0}
        .home-membership-community-hero__motion-state:disabled{cursor:default;opacity:.72}
        .home-membership-community-hero__sr-status{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;clip-path:inset(50%)}
        @media(max-width:900px){.home-membership-community-hero__content{grid-template-columns:minmax(0,1fr) minmax(16rem,.72fr);gap:1.25rem}.home-membership-community-hero__copy h2{font-size:clamp(2.5rem,6vw,4.4rem)}.home-membership-community-hero__values{padding:1rem}.home-membership-community-hero__value strong{font-size:.82rem}.home-membership-community-hero__value small{font-size:.69rem}}
        @media(max-width:700px){.home-membership-community-hero{min-height:42rem}.home-membership-community-hero__content{display:flex;align-items:flex-end;padding:5.5rem 1.25rem 5.2rem}.home-membership-community-hero__image-wash{background:linear-gradient(180deg,rgba(5,9,20,.28),rgba(5,9,20,.78) 45%,rgba(5,9,20,.98) 100%)}.home-membership-community-hero__slide--membership .home-membership-community-hero__image-wash{background:linear-gradient(180deg,rgba(5,9,20,.08),rgba(5,9,20,.8) 44%,rgba(5,9,20,.98) 100%)}.home-membership-community-hero__copy{max-width:none}.home-membership-community-hero__copy h2,.home-membership-community-hero__copy--story h2,.home-membership-community-hero__copy--announcement h2{font-size:clamp(2.45rem,12vw,4.1rem)}.home-membership-community-hero__body{font-size:.98rem}.home-membership-community-hero__values{margin-top:1.6rem}.home-membership-community-hero__copy--story,.home-membership-community-hero__copy--announcement{align-self:flex-end;padding-bottom:0}.home-membership-community-hero__story{font-size:1rem}.home-membership-community-hero__controls{right:1.25rem;bottom:1rem;left:1.25rem}.home-membership-community-hero__motion-state{width:2.75rem;min-width:2.75rem;padding:0;font-size:0}}
        @media(max-width:450px){.home-membership-community-hero{min-height:46rem}.home-membership-community-hero__actions{flex-direction:column;align-items:stretch}.home-membership-community-hero__actions a{width:100%}.home-membership-community-hero__values{gap:.3rem}.home-membership-community-hero__value{padding:.48rem 0}.home-membership-community-hero__value>span{width:2.5rem;height:2.5rem}.home-membership-community-hero__value strong{font-size:.79rem}.home-membership-community-hero__value small{font-size:.67rem}}
        @media(prefers-reduced-motion:reduce){.home-membership-community-hero__slide,.home-membership-community-hero__arrow,.home-membership-community-hero__dots button{transition:none}}
      `}</style>
    </section>
  );
}